<template>
  <div class="referred min-h-screen bg-gray-50 px-4 py-6 pb-28" dir="rtl">
    <!-- Filters -->
    <div class="max-w-2xl mx-auto">
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="grid grid-cols-12 gap-4 items-end">
          <div class="col-span-12 sm:col-span-5">
            <label class="block text-sm text-gray-600 mb-1">از تاریخ</label>
            <date-picker v-model="start_date"></date-picker>
          </div>
          <div class="col-span-12 sm:col-span-5">
            <label class="block text-sm text-gray-600 mb-1">تا تاریخ</label>
            <date-picker v-model="end_date"></date-picker>
          </div>
          <div class="col-span-12 sm:col-span-2">
            <button @click="getReports" class="w-full inline-flex items-center justify-center h-10 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 110-16 8 8 0 010 16z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Content Cards -->
      <div class="mt-6 space-y-4">
        <!-- Branch Sales -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-3 p-4">
            <div class="soft-blob w-14 h-12 text-white flex items-center justify-center" style="--g1:#6ee7b7; --g2:#10b981;">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 17l6-6 4 4 8-8" />
              </svg>
            </div>
            <div class="w-full">
              <div class="text-sm font-bold text-gray-900">فروش شعب</div>
            </div>
            <div class="text-emerald-700 font-bold text-sm">
              {{ get_currency(calculate_items(branch_sales,'TotalAmount')) }}
            </div>
            <button @click="show_sale_branches = !show_sale_branches" class="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-50">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="show_sale_branches" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <div v-show="show_sale_branches" class="border-t border-gray-100">
            <div class="p-4">
              <div v-for="s in branch_sales" :key="s.Title" class="flex items-center justify-between py-2 text-sm">
                <span class="text-gray-700">{{ s.Title }}</span>
                <span class="font-medium text-gray-900">{{ get_currency(s.TotalAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Branch Buys -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-3 p-4">
            <div class="soft-blob w-14 h-12 text-white flex items-center justify-center" style="--g1:#fdba74; --g2:#f97316;">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7h4l2 10h10l2-6H9" />
              </svg>
            </div>
            <div class="w-full">
              <div class="text-sm font-bold text-gray-900">خرید شعب</div>
            </div>
            <div class="text-orange-700 font-bold text-sm">
              {{ get_currency(calculate_items(branch_buys,'TotalAmount')) }}
            </div>
            <button @click="show_buy_branches = !show_buy_branches" class="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-50">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="show_buy_branches" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <div v-show="show_buy_branches" class="border-t border-gray-100">
            <div class="p-4">
              <div v-for="s in branch_buys" :key="s.Title" class="flex items-center justify-between py-2 text-sm">
                <span class="text-gray-700">{{ s.Title }}</span>
                <span class="font-medium text-gray-900">{{ get_currency(s.TotalAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Branch Costs -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-3 p-4">
            <div class="soft-blob w-14 h-12 text-white flex items-center justify-center" style="--g1:#fca5a5; --g2:#e11d48;">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12H9m9 0a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>
            </div>
            <div class="w-full">
              <div class="text-sm font-bold text-gray-900">هزینه شعب</div>
            </div>
            <div class="text-rose-700 font-bold text-sm">
              {{ get_currency(calculate_items(branch_cost,'TotalAmount')) }}
            </div>
            <button @click="show_cost_branches = !show_cost_branches" class="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-50">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="show_cost_branches" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <div v-show="show_cost_branches" class="border-t border-gray-100">
            <div class="p-4">
              <div v-for="s in branch_cost" :key="s.Title" class="flex items-center justify-between py-2 text-sm">
                <span class="text-gray-700">{{ s.Title }}</span>
                <span class="font-medium text-gray-900">{{ get_currency(s.TotalAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Terminal Sales -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-3 p-4">
            <div class="soft-blob w-14 h-12 text-white flex items-center justify-center" style="--g1:#93c5fd; --g2:#3b82f6;">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7h18M3 12h18M7 17h6" />
              </svg>
            </div>
            <div class="w-full">
              <div class="text-sm font-bold text-gray-900">آمار دستگاه پوز ها</div>
            </div>
            <div class="text-blue-700 font-bold text-sm">
              {{ get_currency(calculate_items(terminal_sales,'TotalAmount')) }}
            </div>
            <button @click="show_sale_terminal = !show_sale_terminal" class="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-50">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="show_sale_terminal" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <div v-show="show_sale_terminal" class="border-t border-gray-100">
            <div class="p-4">
              <div v-for="s in terminal_sales" :key="s.Title" class="flex items-center justify-between py-2 text-sm">
                <span class="text-gray-700">{{ s.Title }}</span>
                <span class="font-medium text-gray-900">{{ get_currency(s.TotalAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bank Balance -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-3 p-4">
            <div class="soft-blob w-14 h-12 text-white flex items-center justify-center" style="--g1:#99f6e4; --g2:#14b8a6;">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10l9-6 9 6v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div class="w-full">
              <div class="text-sm font-bold text-gray-900">گزارش حساب بانکی</div>
            </div>
            <div class="text-teal-700 font-bold text-sm">
              {{ get_currency(calculate_items(bank_balance,'balance')) }}
            </div>
            <button @click="show_bank_balance = !show_bank_balance" class="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-50">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="show_bank_balance" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <div v-show="show_bank_balance" class="border-t border-gray-100">
            <div class="p-4 space-y-4">
              <div v-for="s in bank_balance" :key="s.RecId" class="text-sm">
                <div class="mb-2 font-medium text-gray-900">نام حساب: {{ s.Title }}</div>
                <div class="overflow-hidden rounded-lg border border-gray-200">
                  <table class="w-full text-gray-700">
                    <thead class="bg-gray-50 text-xs text-gray-500">
                      <tr>
                        <th class="text-right p-3 font-medium">مانده از قبل</th>
                        <th class="text-right p-3 font-medium">بدهکار</th>
                        <th class="text-right p-3 font-medium">بستانکار</th>
                      </tr>
                    </thead>
                    <tbody class="text-sm">
                      <tr class="bg-white">
                        <td class="p-3">{{ get_currency(s.balance_before) }}</td>
                        <td class="p-3">{{ get_currency(s.Debtors) }}</td>
                        <td class="p-3">{{ get_currency(s.Creditors) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="mt-2 text-gray-900">مانده: {{ get_currency(s.balance) }}</div>
                <hr class="my-3 border-gray-200">
              </div>
            </div>
          </div>
        </div>

        <!-- Branch Requests -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-3 p-4">
            <div class="soft-blob w-14 h-12 text-white flex items-center justify-center" style="--g1:#c4b5fd; --g2:#7c3aed;">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5h9M7 9h11M7 13h11M7 17h7" />
              </svg>
            </div>
            <div class="w-full">
              <div class="text-sm font-bold text-gray-900">درخواست های شعب</div>
            </div>
            <div class="text-purple-700 font-bold text-sm">
              {{ get_currency(calculate_items(branch_requests,'Count')) }}
            </div>
            <button @click="show_branch_requests = !show_branch_requests" class="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-50">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="show_branch_requests" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <div v-show="show_branch_requests" class="border-t border-gray-100">
            <div class="p-4">
              <div v-for="s in branch_requests" :key="s.Title" class="flex items-center justify-between py-2 text-sm">
                <span class="text-gray-700">{{ s.Title }}</span>
                <span class="font-medium text-gray-900">{{ get_currency(s.Count) }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

</template>
<style scoped>
.soft-blob {
  border-radius: 16px;
  background: linear-gradient(145deg, var(--g1, #a78bfa), var(--g2, #7c3aed));
  /* no shadow per request */
}
</style>
<script>
  // import the styles

  import axios from 'axios'
  import Vue from 'vue'

  import {bus} from '../../main'
  import moment from 'moment-jalaali'

  export default {
    props: [
      'passing_data'
    ],
    data() {
      return {
        show_sale_branches: false,
        show_buy_branches: false,
        show_cost_branches: false,
        show_sale_terminal: false,
        show_branch_requests: false,
        show_bank_balance: false,
        branch_sales: [],
        branch_buys: [],
        branch_cost: [],
        terminal_sales: [],
        branch_requests: [],
        bank_balance: [],

        start_date: '',
        end_date: '',
      }
    },
    computed: {},
    components: {},
    watch: {},
    mounted() {
      this.start_date = moment().utc().format('jYYYY/jMM/jDD');
      this.end_date = moment().utc().format('jYYYY/jMM/jDD');

    },

    methods: {

      getReports() {
        // api/AppWebService/GetBranchReq?firstDate=1401/01/23&secondDate=1401/01/30
        //
        //   در خواست ها براساس شعب
        // ------------------------------------------
        //   api/AppWebService/GetReqDay?firstDate=1401/01/23&secondDate=1401/01/30
        //
        //   درخواست های براساس تاریخ مثلا در 7 روز گذشته
        // ------------------------------------------------
        //   api/AppWebService/GetTerminalBalance?firstDate=1401/01/23&secondDate=1401/01/30
        //
        //   فروش هایی ک در پایانه ها تسویه شدن
        // --------------------------------------------------
        //   api/AppWebService/GetBranchSales?firstDate=1401/01/23&secondDate=1401/01/30
        //
        //   فروش شعب
        // --------------------------------------------------
        this.get_branch_sales(151)// forosh
        this.get_branch_sales(102)// buy
        this.get_branch_sales(105)// cost
        this.get_branch_sales()
        this.get_terminal_sales()
        this.get_branch_requests()
        this.get_bank_balance()
      },
      get_branch_sales(type) {
        // this.request_items = [];
        var self = this
// sale 151 buy 102 cost 105
        var url = "https://panel.zoodkomak.com/api/AppWebService/GetBranchSales?docType=" + type + "&firstDate=" + this.start_date + "&secondDate=" + this.end_date
        console.log('pre request called')
        // this.axios.post('/Register' , send_data,{
        this.axios.post(url, {}, {})
          .then(response => {
            if (type == 151) {
              self.branch_sales = []
              self.branch_sales = response.data
              self.branch_sales.sort(function (a, b) {
                return b.TotalAmount - a.TotalAmount;
              });
            }
            if (type == 102) {
              self.branch_buys = []
              self.branch_buys = response.data
              self.branch_buys.sort(function (a, b) {
                return b.TotalAmount - a.TotalAmount;
              });
            }
            if (type == 105) {
              self.branch_cost = []
              self.branch_cost = response.data
              self.branch_cost.sort(function (a, b) {
                return b.TotalAmount - a.TotalAmount;
              });
            }

          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_terminal_sales() {
        // this.request_items = [];
        var self = this

        var url = "https://panel.zoodkomak.com/api/AppWebService/GetTerminalBalance?firstDate=" + this.start_date + "&secondDate=" + this.end_date
        console.log('pre request called')
        // this.axios.post('/Register' , send_data,{
        this.axios.post(url, {}, {})
          .then(response => {
            self.terminal_sales = []
            self.terminal_sales = response.data
            self.terminal_sales.sort(function (a, b) {
              return b.TotalAmount - a.TotalAmount;
            });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_branch_requests() {
        // this.request_items = [];
        var self = this

        var url = "https://panel.zoodkomak.com/api/AppWebService/GetBranchReq?firstDate=" + this.start_date + "&secondDate=" + this.end_date
        console.log('pre request called')
        // this.axios.post('/Register' , send_data,{
        this.axios.post(url, {}, {})
          .then(response => {
            self.branch_requests = []
            self.branch_requests = response.data
            self.branch_requests.sort(function (a, b) {
              return b.Count - a.Count;
            });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_bank_balance() {
        // this.request_items = [];
        var self = this

        var url = "https://panel.zoodkomak.com/api/AppWebService/GetBankBalance?firstDate=" + this.start_date + "&secondDate=" + this.end_date
        console.log('pre request called')
        // this.axios.post('/Register' , send_data,{
        this.axios.post(url, {}, {})
          .then(response => {
            self.bank_balance = []
            var data = JSON.parse(response.data)
            console.log("data is",data)
            data.DetailBalance.forEach(function (val) {
              var balance_before = data.BankBalances.filter(x => x.RecId == val.RecId)[0].Balance;
              var balance = balance_before + (val.Debtors + val.Creditors)
              self.bank_balance.push({
                Title: val.Title,
                balance_before: balance_before,
                Creditors: val.Creditors,
                Debtors: val.Debtors,
                RecId: val.RecId,
                balance: balance
              })
            })
            // self.bank_balance.sort(function(a, b) {
            //   return b.Count - a.Count;
            // });
          }).catch(e => {
          this.errors.push(e)
        })
      },

    }
  }
</script>
